import type { TrialMigrationExportOutput } from '@chorepop/core/client';
import { DAILY_GBP_QUOTE_CURRENCY_CODES } from '@chorepop/core/domain';
import { type Session, type SupabaseClient, createClient } from '@supabase/supabase-js';
import { invoke } from '@tauri-apps/api/core';
import { getCurrent, onOpenUrl } from '@tauri-apps/plugin-deep-link';
import { openUrl } from '@tauri-apps/plugin-opener';

declare const CHOREPOP_PUBLIC_DEMO: boolean;

export type MobilePlatform = 'android' | 'fireos' | 'ios' | 'other';
export type CloudAuthProvider = 'google' | 'apple';
export type CloudEnvironment = 'staging' | 'production';

export type CloudGuardianUser = {
  id: string;
  email: string | null;
};

export type CloudFamilyMembership = {
  familyId: string;
  role: 'owner' | 'guardian' | 'child';
};

export type CloudFamilySummary = {
  id: string;
  name: string;
  baseCurrency: string;
};

export type CloudFamilyAccount = {
  id: string;
  familyId: string;
  kind: 'guardian' | 'child';
  displayName: string;
  authUserId: string | null;
};

export type CloudGuardianBootstrap =
  | {
      kind: 'unlinked';
      user: CloudGuardianUser;
    }
  | {
      kind: 'family_selection_required';
      user: CloudGuardianUser;
      families: CloudFamilySummary[];
    }
  | {
      kind: 'ready';
      user: CloudGuardianUser;
      family: CloudFamilySummary;
      membershipRole: 'owner' | 'guardian';
      guardianAccount: CloudFamilyAccount;
      accounts: CloudFamilyAccount[];
    };

export type CloudGuardianGateway = {
  getVerifiedUser: () => Promise<CloudGuardianUser>;
  listMemberships: (userId: string) => Promise<CloudFamilyMembership[]>;
  listFamilies: (familyIds: string[]) => Promise<CloudFamilySummary[]>;
  findGuardianAccount: (familyId: string, userId: string) => Promise<CloudFamilyAccount | null>;
  listAccounts: (familyId: string) => Promise<CloudFamilyAccount[]>;
};

export type CloudFamilySyncPayload = {
  importId: string;
  rows: Array<{
    sourceTable: string;
    sourceOrdinal: number;
    sourceRowJson: string;
    sourceRowSha256: string;
  }>;
};

/**
 * The only cloud state a free, single-device trial may read or write. It is
 * intentionally separate from every family-ledger table: an authenticated
 * trial guardian has no family membership, account, bucket, chore, history,
 * or FX data in Supabase until a verified subscription migration succeeds.
 */
export type CloudTrialLifecycle =
  | 'trial_active'
  | 'subscription_required'
  | 'subscribed'
  | 'deletion_requested';

export type CloudTrialStatus = {
  lifecycleState: CloudTrialLifecycle;
  trialStartedAt: string;
  trialExpiresAt: string;
  cloudFamilyId: string | null;
};

export type CloudTrialGateway = {
  startOrResumeTrial: (deviceId: string) => Promise<unknown>;
};

export type VerifiedSubscriptionMigration = {
  familyId: string;
  guardianAccountId: string;
};

export type EmailTrialCredentials = {
  email: string;
  password: string;
};

export type EmailTrialSignUpResult = {
  session: Session | null;
  emailConfirmationRequired: boolean;
};

export type EmailTrialMfaStatus = {
  emailVerified: boolean;
  verifiedTotpFactorId: string | null;
  currentLevel: 'aal1' | 'aal2' | null;
  nextLevel: 'aal1' | 'aal2' | null;
};

export type EmailTrialTotpEnrollment = {
  factorId: string;
  qrCode: string;
  secret: string;
  uri: string;
};

/**
 * A read-only daily reference rate retrieved from ChorePop Supabase.
 * `ratePpm` is always source currency -> GBP, ready for integer ledger
 * arithmetic. It is never a payment quote or a request to move real money.
 */
export type CloudFxRate = {
  sourceCurrencyCode: string;
  targetCurrencyCode: 'GBP' | 'USD';
  ratePpm: number;
  effectiveDate: string;
  provider: 'frankfurter';
};
export type CloudFxRateToGbp = CloudFxRate;

export const CHOREPOP_CLOUD_ENVIRONMENTS = {
  staging: {
    supabaseUrl: 'https://dfylermzqquxtowrnvia.supabase.co',
    authCallback: 'chorepop-staging://auth/callback',
  },
  production: {
    supabaseUrl: 'https://wzbgmljaxjjhchslkjyn.supabase.co',
    authCallback: 'chorepop://auth/callback',
  },
} as const satisfies Record<
  CloudEnvironment,
  { supabaseUrl: `https://${string}.supabase.co`; authCallback: `${string}://auth/callback` }
>;

/** Public, read-only reference data shared by staging and production. */
export const SHARED_FX_RATES_URL = `${CHOREPOP_CLOUD_ENVIRONMENTS.production.supabaseUrl}/functions/v1/refresh-fx-rates`;

export type CloudEnvironmentConfig = (typeof CHOREPOP_CLOUD_ENVIRONMENTS)[CloudEnvironment];

/**
 * Resolve the cloud environment from build variables without ever accepting a
 * URL or callback outside the checked-in allow-list. An explicit environment
 * wins; when a URL is supplied by a local .env file, infer only from an exact
 * allow-listed URL so existing production development setups keep working.
 */
export function resolveCloudEnvironment(
  requestedEnvironment: string | undefined,
  requestedUrl: string | undefined,
): CloudEnvironment | null {
  const requested = requestedEnvironment?.trim();
  if (requested === 'staging' || requested === 'production') return requested;
  if (requested && requested !== '') return null;

  const url = requestedUrl?.trim().replace(/\/+$/, '');
  if (url === CHOREPOP_CLOUD_ENVIRONMENTS.staging.supabaseUrl) return 'staging';
  if (url === CHOREPOP_CLOUD_ENVIRONMENTS.production.supabaseUrl) return 'production';
  return 'staging';
}

type ViteEnvironment = Record<string, string | boolean | undefined>;

const environment =
  (import.meta as ImportMeta & { env?: ViteEnvironment }).env ?? ({} as ViteEnvironment);
const configuredEnvironment = resolveCloudEnvironment(
  environment.VITE_CHOREPOP_ENVIRONMENT === undefined
    ? undefined
    : String(environment.VITE_CHOREPOP_ENVIRONMENT),
  environment.VITE_SUPABASE_URL === undefined ? undefined : String(environment.VITE_SUPABASE_URL),
);
const selectedEnvironment =
  configuredEnvironment === 'staging' || configuredEnvironment === 'production'
    ? CHOREPOP_CLOUD_ENVIRONMENTS[configuredEnvironment]
    : null;
const configuredUrl = String(
  environment.VITE_SUPABASE_URL ?? selectedEnvironment?.supabaseUrl ?? '',
)
  .trim()
  .replace(/\/+$/, '');
const configuredAuthCallback = String(
  environment.VITE_CHOREPOP_AUTH_CALLBACK ?? selectedEnvironment?.authCallback ?? '',
).trim();
const configuredPublishableKey = String(environment.VITE_SUPABASE_PUBLISHABLE_KEY ?? '').trim();
// Native providers return an OIDC ID token directly to the app. The web client
// ID is the token audience Supabase verifies; the platform client ID is only
// used by Google's native SDK on iOS (Android Credential Manager identifies the
// installed app from its package/signing certificate).
const configuredGoogleWebClientId = String(environment.VITE_GOOGLE_WEB_CLIENT_ID ?? '').trim();
const configuredGoogleAndroidClientId = String(
  environment.VITE_GOOGLE_ANDROID_CLIENT_ID ?? configuredGoogleWebClientId,
).trim();
const configuredGoogleIosClientId = String(environment.VITE_GOOGLE_IOS_CLIENT_ID ?? '').trim();

export const CHOREPOP_AUTH_CALLBACK = configuredAuthCallback;
export const CHOREPOP_SUPABASE_URL = configuredUrl;

export function getConfiguredCloudEnvironment(): CloudEnvironment {
  if (!configuredEnvironment) {
    throw new Error('ChorePop does not have a valid staging or production environment.');
  }
  return configuredEnvironment;
}

let cloudClient: SupabaseClient | null = null;

export function detectMobilePlatform(
  userAgent = typeof navigator === 'undefined' ? '' : navigator.userAgent,
): MobilePlatform {
  if (/iphone|ipad|ipod/i.test(userAgent)) return 'ios';
  // iPadOS can advertise the desktop Safari user agent (`Macintosh`) even
  // though it is running in an iPad WKWebView. Without this compatibility
  // check, the tablet setup screen hides both native sign-in choices.
  if (
    /macintosh/i.test(userAgent) &&
    typeof navigator !== 'undefined' &&
    navigator.maxTouchPoints > 1
  ) {
    return 'ios';
  }
  if (/android/i.test(userAgent)) {
    // Fire tablets identify themselves as Android devices but do not ship
    // Google Play Services/Credential Manager providers. Keep Google sign-in
    // available for them through the Supabase OAuth/deep-link path instead of
    // invoking the native Credential Manager plugin.
    if (/silk\/|fire os|;\s*kf[a-z0-9]+(?:\s+build\/|\s*[;)])/i.test(userAgent)) {
      return 'fireos';
    }
    return 'android';
  }
  return 'other';
}

export function nativeAuthAvailable(platform = detectMobilePlatform()): boolean {
  return platform === 'android' || platform === 'ios';
}

export function nativeGoogleClientId(platform: MobilePlatform): string {
  if (platform === 'ios') return configuredGoogleIosClientId;
  if (platform === 'android') return configuredGoogleAndroidClientId;
  return '';
}

export function googleSignInTransport(
  platform: MobilePlatform,
  webClientId = configuredGoogleWebClientId,
  platformClientId = nativeGoogleClientId(platform),
): 'native' | 'oauth' {
  const webId = webClientId.trim();
  const nativeId = platformClientId.trim();
  if (platform !== 'ios' && platform !== 'android') return 'oauth';

  // Google requires distinct OAuth client types for the installed app and its
  // backend audience. Passing a Web client as GIDConfiguration.clientID makes
  // Google reject the iOS custom callback scheme with error 400. When a real
  // platform client is unavailable, use Supabase's PKCE browser/deep-link flow:
  // Google then sees only Supabase's HTTPS callback and the app receives the
  // final one-time code through its allow-listed ChorePop scheme.
  return webId.length > 0 && nativeId.length > 0 && nativeId !== webId ? 'native' : 'oauth';
}

export function nativeGoogleAuthConfigured(platform: MobilePlatform): boolean {
  return googleSignInTransport(platform) === 'native';
}

export function nativeAppleAuthConfigured(platform: MobilePlatform): boolean {
  return platform === 'ios';
}

export function providersForPlatform(platform: MobilePlatform): CloudAuthProvider[] {
  if (platform === 'android' || platform === 'fireos') return ['google'];
  if (platform === 'ios') return ['apple', 'google'];
  return [];
}

export function isCloudAuthConfigured(): boolean {
  // The marketing-site demo runs the real app UI against an in-memory backend.
  // It must never open a Supabase session, even when a developer has valid
  // cloud configuration in their shell. Native builds never request this URL.
  const environment = (
    import.meta as ImportMeta & { env?: Record<string, string | boolean | undefined> }
  ).env;
  const publicDemoBuild = typeof CHOREPOP_PUBLIC_DEMO !== 'undefined' && CHOREPOP_PUBLIC_DEMO;
  if (
    typeof window !== 'undefined' &&
    new URLSearchParams(window.location.search).get('chorepopDemo') === '1' &&
    (environment?.DEV === true || publicDemoBuild)
  ) {
    return false;
  }
  return (
    selectedEnvironment !== null &&
    configuredUrl === selectedEnvironment.supabaseUrl &&
    configuredAuthCallback === selectedEnvironment.authCallback &&
    configuredPublishableKey.length > 0 &&
    !configuredPublishableKey.includes('replace_me')
  );
}

export function getCloudAuthProjectUrl(): string {
  if (!isCloudAuthConfigured()) {
    throw new Error('ChorePop cloud sign-in is not configured for this build.');
  }
  return configuredUrl;
}

export function getCloudAuthPublishableKey(): string {
  if (!isCloudAuthConfigured()) {
    throw new Error('ChorePop cloud sign-in is not configured for this build.');
  }
  return configuredPublishableKey;
}

export function getCloudAuthClient(): SupabaseClient {
  if (!isCloudAuthConfigured()) {
    throw new Error('ChorePop cloud sign-in is not configured for this build.');
  }
  cloudClient ??= createClient(configuredUrl, configuredPublishableKey, {
    auth: {
      autoRefreshToken: true,
      detectSessionInUrl: false,
      flowType: 'pkce',
      persistSession: true,
    },
  });
  return cloudClient;
}

function queryError(context: string, error: { message: string } | null): void {
  if (error) throw new Error(`${context}: ${error.message}`);
}

function asRecord(value: unknown, context: string): Record<string, unknown> {
  if (typeof value !== 'object' || value === null || Array.isArray(value)) {
    throw new Error(`${context} returned an invalid record.`);
  }
  return value as Record<string, unknown>;
}

function requiredString(record: Record<string, unknown>, key: string, context: string): string {
  const value = record[key];
  if (typeof value !== 'string' || value.trim() === '') {
    throw new Error(`${context} is missing ${key}.`);
  }
  return value;
}

function optionalString(record: Record<string, unknown>, key: string): string | null {
  const value = record[key];
  if (value === null || value === undefined) return null;
  if (typeof value !== 'string') throw new Error(`Invalid ${key}.`);
  return value.trim() === '' ? null : value;
}

function parseMembership(value: unknown): CloudFamilyMembership {
  const row = asRecord(value, 'Family membership');
  const role = requiredString(row, 'role', 'Family membership');
  if (role !== 'owner' && role !== 'guardian' && role !== 'child') {
    throw new Error('Family membership returned an unsupported role.');
  }
  return {
    familyId: requiredString(row, 'family_id', 'Family membership'),
    role,
  };
}

function parseFamily(value: unknown): CloudFamilySummary {
  const row = asRecord(value, 'Family');
  const baseCurrency = requiredString(row, 'base_currency', 'Family');
  if (!/^[A-Z]{3}$/.test(baseCurrency)) {
    throw new Error('Family returned an invalid base currency.');
  }
  return {
    id: requiredString(row, 'id', 'Family'),
    name: requiredString(row, 'name', 'Family'),
    baseCurrency,
  };
}

function parseFamilyAccount(value: unknown): CloudFamilyAccount {
  const row = asRecord(value, 'Family account');
  const kind = requiredString(row, 'kind', 'Family account');
  if (kind !== 'guardian' && kind !== 'child') {
    throw new Error('Family account returned an unsupported kind.');
  }
  const authUserId = row.auth_user_id;
  if (authUserId !== null && typeof authUserId !== 'string') {
    throw new Error('Family account returned an invalid auth user ID.');
  }
  return {
    id: requiredString(row, 'id', 'Family account'),
    familyId: requiredString(row, 'family_id', 'Family account'),
    kind,
    displayName: requiredString(row, 'display_name', 'Family account'),
    authUserId,
  };
}

function parseTrialStatus(value: unknown): CloudTrialStatus {
  const row = asRecord(value, 'Cloud trial');
  const lifecycleState = requiredString(row, 'lifecycle_state', 'Cloud trial');
  if (
    lifecycleState !== 'trial_active' &&
    lifecycleState !== 'subscription_required' &&
    lifecycleState !== 'subscribed' &&
    lifecycleState !== 'deletion_requested'
  ) {
    throw new Error('Cloud trial returned an unsupported lifecycle state.');
  }
  const cloudFamilyId = row.cloud_family_id;
  if (cloudFamilyId !== null && typeof cloudFamilyId !== 'string') {
    throw new Error('Cloud trial returned an invalid family identifier.');
  }
  if (lifecycleState === 'trial_active' && cloudFamilyId !== null) {
    throw new Error('An active local trial must not have cloud family data.');
  }
  return {
    lifecycleState,
    trialStartedAt: requiredString(row, 'trial_started_at', 'Cloud trial'),
    trialExpiresAt: requiredString(row, 'trial_expires_at', 'Cloud trial'),
    cloudFamilyId,
  };
}

export function createSupabaseCloudTrialGateway(client = getCloudAuthClient()): CloudTrialGateway {
  return {
    async startOrResumeTrial(deviceId) {
      const { data, error } = await client.rpc('trial_entitlement_resolve', {
        p_device_id: deviceId,
      });
      queryError('Could not start the single-device trial', error);
      if (!Array.isArray(data) || data.length !== 1) {
        throw new Error('Cloud trial returned an invalid response.');
      }
      return data[0];
    },
  };
}

/**
 * Starts a server-timed trial for an authenticated guardian, or resumes it
 * from the same device. The RPC rejects a second device while the trial is
 * active, so changing a phone clock cannot extend the free period.
 */
export async function startOrResumeCloudTrial(
  deviceId: string,
  gateway: CloudTrialGateway = createSupabaseCloudTrialGateway(),
): Promise<CloudTrialStatus> {
  if (!/^[0-9a-f]{8}-(?:[0-9a-f]{4}-){3}[0-9a-f]{12}$/i.test(deviceId)) {
    throw new Error('This device has an invalid trial identifier.');
  }
  return parseTrialStatus(await gateway.startOrResumeTrial(deviceId));
}

export async function requestTrialDataDeletion(client = getCloudAuthClient()): Promise<string> {
  const { data, error } = await client.rpc('request_trial_data_deletion');
  queryError('Could not schedule trial data deletion', error);
  if (!Array.isArray(data) || data.length !== 1) {
    throw new Error('The trial deletion request returned an invalid recovery deadline.');
  }
  return requiredString(
    asRecord(data[0], 'Trial deletion request'),
    'delete_after',
    'Trial deletion request',
  );
}

export async function cancelTrialDataDeletion(client = getCloudAuthClient()): Promise<void> {
  const { error } = await client.rpc('cancel_trial_data_deletion');
  queryError('Could not recover trial data', error);
}

function assertUuid(value: unknown, label: string): string {
  if (typeof value !== 'string' || !/^[0-9a-f]{8}-(?:[0-9a-f]{4}-){3}[0-9a-f]{12}$/iu.test(value)) {
    throw new Error(`${label} returned an invalid identifier.`);
  }
  return value;
}

/**
 * Sends the store proof to the trusted verification function, uploads only
 * credential-free canonical ledger rows, and asks Postgres to reconcile and
 * create the subscribed family atomically. A native purchase alone never
 * grants access.
 */
export async function completeVerifiedTrialSubscription(input: {
  store: 'apple' | 'google';
  purchase: { productId: string; purchaseToken: string; jwsRepresentation?: string };
  deviceId: string;
  platform: 'android' | 'ios';
  migration: TrialMigrationExportOutput;
  client?: SupabaseClient;
}): Promise<VerifiedSubscriptionMigration> {
  const client = input.client ?? getCloudAuthClient();
  const { data: verificationValue, error: verificationError } = await client.functions.invoke(
    'verify-subscription',
    {
      body: {
        store: input.store,
        productId: input.purchase.productId,
        purchaseToken: input.purchase.purchaseToken,
        ...(input.purchase.jwsRepresentation
          ? { jwsRepresentation: input.purchase.jwsRepresentation }
          : {}),
        migration: {
          sourceDeviceId: input.deviceId,
          sourceDatabaseSha256: input.migration.sourceDatabaseSha256,
          sourceSchemaVersion: input.migration.sourceSchemaVersion,
          sourceManifest: input.migration.sourceManifest,
          expectedTableCount: input.migration.tables.length,
          expectedRowCount: input.migration.rows.length,
          guardianLocalAccountId: input.migration.guardianLocalAccountId,
          familyName: input.migration.familyName,
          platform: input.platform,
        },
      },
    },
  );
  if (verificationError)
    throw new Error(`The store purchase could not be verified: ${verificationError.message}`);
  const verification = asRecord(verificationValue, 'Subscription verification');
  const uploadId = assertUuid(verification.uploadId, 'Subscription verification');
  const state = requiredString(verification, 'state', 'Subscription verification');

  if (state !== 'finalized') {
    if (state !== 'uploading') throw new Error('The trial migration is not available.');
    const manifests = input.migration.tables.map((table) => ({
      upload_id: uploadId,
      source_table: table.sourceTable,
      row_count: table.rowCount,
      source_table_sha256: table.sourceTableSha256,
      primary_key_columns: table.primaryKeyColumns,
      canonicalization_version: 1,
    }));
    const { error: manifestError } = await client
      .from('trial_migration_table_manifests')
      .upsert(manifests, { onConflict: 'upload_id,source_table', ignoreDuplicates: true });
    queryError('Could not upload the trial table manifest', manifestError);

    const pageSize = 250;
    for (let offset = 0; offset < input.migration.rows.length; offset += pageSize) {
      const page = input.migration.rows.slice(offset, offset + pageSize).map((row) => ({
        upload_id: uploadId,
        source_table: row.sourceTable,
        source_ordinal: row.sourceOrdinal,
        source_key: row.sourceKey,
        source_row_canonical: row.sourceRowCanonical,
        source_row_sha256: row.sourceRowSha256,
      }));
      const { error } = await client.from('trial_migration_rows').upsert(page, {
        onConflict: 'upload_id,source_table,source_ordinal',
        ignoreDuplicates: true,
      });
      queryError('Could not upload the encrypted-trial ledger migration', error);
    }
    const { data, error } = await client.rpc('finalize_trial_subscription_migration', {
      p_upload_id: uploadId,
    });
    queryError('Could not finalize the subscribed family', error);
    if (!Array.isArray(data) || data.length !== 1) {
      throw new Error('The subscribed family returned an invalid migration result.');
    }
    const row = asRecord(data[0], 'Subscribed family');
    const familyId = assertUuid(row.family_id, 'Subscribed family');
    const { error: familyCurrencyError } = await client
      .from('families')
      .update({ base_currency: input.migration.familyCurrency })
      .eq('id', familyId);
    queryError('Could not set the subscribed family currency', familyCurrencyError);
    return {
      familyId,
      guardianAccountId: assertUuid(row.guardian_account_id, 'Subscribed family'),
    };
  }

  const bootstrap = await bootstrapCloudGuardian();
  if (bootstrap.kind !== 'ready') throw new Error('The subscribed family could not be reopened.');
  const { error: familyCurrencyError } = await client
    .from('families')
    .update({ base_currency: input.migration.familyCurrency })
    .eq('id', bootstrap.family.id);
  queryError('Could not set the subscribed family currency', familyCurrencyError);
  return { familyId: bootstrap.family.id, guardianAccountId: bootstrap.guardianAccount.id };
}

/** Keep an already-linked cloud family's metadata aligned with the local
 * all-account currency switch. Returns false when there is no active cloud
 * identity, so an offline-only family remains fully usable. */
export async function updateCloudFamilyCurrencyIfLinked(
  newCurrencyCode: 'GBP' | 'USD',
): Promise<boolean> {
  let client: SupabaseClient;
  try {
    client = getCloudAuthClient();
  } catch {
    return false;
  }
  const { data: userData, error: userError } = await client.auth.getUser();
  if (userError || !userData.user) return false;
  const { data: memberships, error: membershipError } = await client
    .from('family_memberships')
    .select('family_id, role')
    .eq('user_id', userData.user.id)
    .in('role', ['owner', 'guardian']);
  queryError('Could not find the linked family', membershipError);
  if (!Array.isArray(memberships) || memberships.length === 0) return false;
  if (memberships.length !== 1) {
    throw new Error('Choose a single cloud family before switching its currency.');
  }
  const membership = asRecord(memberships[0], 'Family membership');
  const familyId = assertUuid(membership.family_id, 'Family membership');
  const { error } = await client
    .from('families')
    .update({ base_currency: newCurrencyCode })
    .eq('id', familyId);
  queryError('Could not switch the linked family currency', error);
  return true;
}

function assertEmailTrialCredentials(input: EmailTrialCredentials): EmailTrialCredentials {
  const email = input.email.trim().toLowerCase();
  if (!/^\S+@\S+\.\S+$/.test(email)) throw new Error('Enter a valid email address.');
  if (input.password.length < 10) {
    throw new Error('Choose an email sign-in password with at least 10 characters.');
  }
  return { email, password: input.password };
}

/**
 * Creates an email/password cloud identity for a local-only trial. A family
 * ledger is never created here. When Supabase email confirmation is enabled,
 * the app waits for the authenticated deep-link callback before it can start
 * the server-timed trial.
 */
export async function beginEmailTrialSignUp(
  input: EmailTrialCredentials,
  client = getCloudAuthClient(),
): Promise<EmailTrialSignUpResult> {
  const credentials = assertEmailTrialCredentials(input);
  const { data, error } = await client.auth.signUp({
    email: credentials.email,
    password: credentials.password,
    options: { emailRedirectTo: CHOREPOP_AUTH_CALLBACK },
  });
  if (error) throw error;
  return {
    session: data.session,
    emailConfirmationRequired: data.session === null,
  };
}

/** Sign into an existing email/password trial identity at AAL1. */
export async function beginEmailTrialSignIn(
  input: EmailTrialCredentials,
  client = getCloudAuthClient(),
): Promise<Session> {
  const credentials = assertEmailTrialCredentials(input);
  const { data, error } = await client.auth.signInWithPassword(credentials);
  if (error) throw error;
  if (!data.session) throw new Error('Email sign-in did not create a cloud session.');
  return data.session;
}

/**
 * Reads the email and TOTP state needed to enforce REQ-1.17. This deliberately
 * uses Supabase's factor list and assurance-level APIs rather than trusting a
 * local flag or a decoded client-side token.
 */
export async function getEmailTrialMfaStatus(
  client = getCloudAuthClient(),
): Promise<EmailTrialMfaStatus> {
  const [userResult, factorsResult, assuranceResult] = await Promise.all([
    client.auth.getUser(),
    client.auth.mfa.listFactors(),
    client.auth.mfa.getAuthenticatorAssuranceLevel(),
  ]);
  if (userResult.error) throw userResult.error;
  if (factorsResult.error) throw factorsResult.error;
  if (assuranceResult.error) throw assuranceResult.error;
  const user = userResult.data.user;
  if (!user) throw new Error('The cloud session did not contain a verified user.');
  const verifiedTotp = factorsResult.data.totp[0];
  const current = assuranceResult.data.currentLevel;
  const next = assuranceResult.data.nextLevel;
  return {
    emailVerified: Boolean(user.email_confirmed_at),
    verifiedTotpFactorId: verifiedTotp?.id ?? null,
    currentLevel: current === 'aal2' ? 'aal2' : current === 'aal1' ? 'aal1' : null,
    nextLevel: next === 'aal2' ? 'aal2' : next === 'aal1' ? 'aal1' : null,
  };
}

/** Enrols a ChorePop TOTP factor and returns its one-time setup material. */
export async function enrollEmailTrialTotp(
  client = getCloudAuthClient(),
): Promise<EmailTrialTotpEnrollment> {
  const { data, error } = await client.auth.mfa.enroll({
    factorType: 'totp',
    friendlyName: 'ChorePop guardian sign-in',
    issuer: 'ChorePop',
  });
  if (error) throw error;
  if (data.type !== 'totp' || !data.totp?.qr_code || !data.totp.secret || !data.totp.uri) {
    throw new Error('Authenticator setup did not return a TOTP secret.');
  }
  return {
    factorId: data.id,
    qrCode: data.totp.qr_code,
    secret: data.totp.secret,
    uri: data.totp.uri,
  };
}

/** Verifies a six-digit TOTP code and upgrades the Supabase session to AAL2. */
export async function verifyEmailTrialTotp(
  input: { factorId: string; code: string },
  client = getCloudAuthClient(),
): Promise<Session> {
  const factorId = input.factorId.trim();
  const code = input.code.replace(/\s/g, '');
  if (!factorId) throw new Error('Authenticator setup is missing. Start again.');
  if (!/^\d{6}$/.test(code))
    throw new Error('Enter the six-digit code from your authenticator app.');
  const { data, error } = await client.auth.mfa.challengeAndVerify({ factorId, code });
  if (error) throw error;
  if (!data.access_token)
    throw new Error('Authenticator verification did not create a secure session.');
  const sessionResult = await client.auth.getSession();
  if (sessionResult.error) throw sessionResult.error;
  if (!sessionResult.data.session) {
    throw new Error('Authenticator verification did not create a secure session.');
  }
  return sessionResult.data.session;
}

export function createSupabaseCloudGuardianGateway(
  client = getCloudAuthClient(),
): CloudGuardianGateway {
  return {
    async getVerifiedUser() {
      // getUser() validates the access token with Supabase Auth. Do not use
      // locally persisted session claims as the authorization boundary.
      const { data, error } = await client.auth.getUser();
      queryError('Could not verify the cloud session', error);
      if (!data.user) throw new Error('The cloud session did not contain a verified user.');
      return { id: data.user.id, email: data.user.email ?? null };
    },

    async listMemberships(userId) {
      const { data, error } = await client
        .from('family_memberships')
        .select('family_id, role')
        .eq('user_id', userId)
        .order('family_id');
      queryError('Could not load family memberships', error);
      if (!Array.isArray(data)) throw new Error('Family memberships returned an invalid list.');
      return data.map(parseMembership);
    },

    async listFamilies(familyIds) {
      if (familyIds.length === 0) return [];
      const { data, error } = await client
        .from('families')
        .select('id, name, base_currency')
        .in('id', familyIds)
        .order('name');
      queryError('Could not load families', error);
      if (!Array.isArray(data)) throw new Error('Families returned an invalid list.');
      return data.map(parseFamily);
    },

    async findGuardianAccount(familyId, userId) {
      const { data, error } = await client
        .from('family_accounts')
        .select('id, family_id, kind, display_name, auth_user_id')
        .eq('family_id', familyId)
        .eq('auth_user_id', userId)
        .eq('kind', 'guardian')
        .eq('is_active', true)
        .maybeSingle();
      queryError('Could not load the guardian account', error);
      return data === null ? null : parseFamilyAccount(data);
    },

    async listAccounts(familyId) {
      const { data, error } = await client
        .from('family_accounts')
        .select('id, family_id, kind, display_name, auth_user_id')
        .eq('family_id', familyId)
        .eq('is_active', true)
        .order('display_name');
      queryError('Could not load family accounts', error);
      if (!Array.isArray(data)) throw new Error('Family accounts returned an invalid list.');
      return data.map(parseFamilyAccount);
    },
  };
}

export async function bootstrapCloudGuardian(options?: {
  gateway?: CloudGuardianGateway;
  expectedUserId?: string;
}): Promise<CloudGuardianBootstrap> {
  const gateway = options?.gateway ?? createSupabaseCloudGuardianGateway();
  const user = await gateway.getVerifiedUser();
  if (options?.expectedUserId && user.id !== options.expectedUserId) {
    throw new Error('The verified cloud user changed while sign-in was completing.');
  }

  const memberships = await gateway.listMemberships(user.id);
  if (memberships.length === 0) return { kind: 'unlinked', user };
  if (memberships.some((membership) => membership.role === 'child')) {
    throw new Error('Cloud sign-in is available only to parent and guardian accounts.');
  }

  const uniqueFamilyIds = [...new Set(memberships.map((membership) => membership.familyId))];
  if (uniqueFamilyIds.length !== memberships.length) {
    throw new Error('The cloud account returned duplicate family memberships.');
  }
  const families = await gateway.listFamilies(uniqueFamilyIds);
  if (families.length !== uniqueFamilyIds.length) {
    throw new Error('The cloud account could not resolve every family membership.');
  }
  if (families.length > 1) return { kind: 'family_selection_required', user, families };

  const family = families[0];
  const membership = memberships[0];
  if (!family || !membership || family.id !== membership.familyId) {
    throw new Error('The cloud family membership did not match the resolved family.');
  }
  if (membership.role === 'child') {
    throw new Error('Cloud sign-in is available only to parent and guardian accounts.');
  }
  const guardianAccount = await gateway.findGuardianAccount(family.id, user.id);
  if (!guardianAccount || guardianAccount.authUserId !== user.id) {
    throw new Error('This cloud identity is not linked to an active ChorePop guardian account.');
  }
  const accounts = await gateway.listAccounts(family.id);
  if (!accounts.some((account) => account.id === guardianAccount.id)) {
    throw new Error('The linked guardian account was missing from the family account list.');
  }

  return {
    kind: 'ready',
    user,
    family,
    membershipRole: membership.role,
    guardianAccount,
    accounts,
  };
}

/**
 * Fetch the RLS-scoped one-time ledger import for a linked family. The
 * publishable client carries the user's Supabase session, so the policies on
 * legacy_imports and legacy_source_rows are the authorization boundary.
 */
export async function fetchCloudFamilySyncPayload(
  familyId: string,
): Promise<CloudFamilySyncPayload> {
  const client = getCloudAuthClient();
  const { data: imports, error: importError } = await client
    .from('legacy_imports')
    .select('id, state')
    .eq('family_id', familyId)
    .limit(2);
  queryError('Could not load the family cloud import', importError);
  if (!Array.isArray(imports) || imports.length !== 1) {
    throw new Error('The cloud family did not have exactly one ledger import.');
  }
  const importRow = asRecord(imports[0], 'Family cloud import');
  const importId = requiredString(importRow, 'id', 'Family cloud import');
  const state = requiredString(importRow, 'state', 'Family cloud import');
  if (state !== 'verified' && state !== 'confirmed') {
    throw new Error('The family cloud import is not ready to sync.');
  }

  const rows: CloudFamilySyncPayload['rows'] = [];
  const pageSize = 500;
  for (let offset = 0; ; offset += pageSize) {
    const { data, error } = await client
      .from('legacy_source_rows')
      .select('source_table, source_ordinal, source_row, source_row_sha256')
      .eq('import_id', importId)
      .order('source_table')
      .order('source_ordinal')
      .range(offset, offset + pageSize - 1);
    queryError('Could not sync the family ledger rows', error);
    if (!Array.isArray(data)) throw new Error('The family ledger sync returned an invalid page.');
    for (const value of data) {
      const row = asRecord(value, 'Family ledger row');
      const sourceRow = row.source_row;
      if (typeof sourceRow !== 'object' || sourceRow === null || Array.isArray(sourceRow)) {
        throw new Error('The family ledger sync returned an invalid source row.');
      }
      const ordinal = row.source_ordinal;
      if (typeof ordinal !== 'number' || !Number.isSafeInteger(ordinal) || ordinal < 0) {
        throw new Error('The family ledger sync returned an invalid row ordinal.');
      }
      rows.push({
        sourceTable: requiredString(row, 'source_table', 'Family ledger row'),
        sourceOrdinal: ordinal,
        sourceRowJson: JSON.stringify(sourceRow),
        sourceRowSha256: requiredString(row, 'source_row_sha256', 'Family ledger row'),
      });
    }
    if (data.length < pageSize) break;
  }
  if (rows.length === 0) throw new Error('The cloud family ledger is empty.');
  return { importId, rows };
}

/**
 * Fetch the newest server-refreshed daily rate for converting an overseas gift
 * or holiday spend into the family's GBP or USD ledger. The installed app
 * talks only to Supabase; the scheduled Edge Function is the sole caller of
 * the external rate provider.
 */
export async function fetchDailyFxRate(
  sourceCurrencyCode: string,
  targetCurrencyCode: 'GBP' | 'USD',
): Promise<CloudFxRate> {
  if (!/^[A-Z]{3}$/.test(sourceCurrencyCode) || sourceCurrencyCode === targetCurrencyCode) {
    throw new Error('Choose a supported foreign currency.');
  }
  const rateSet = await fetchSharedDailyFxRates();
  const sourceRow = sourceCurrencyCode === 'GBP' ? null : rateSet.rates.get(sourceCurrencyCode);
  if (targetCurrencyCode === 'GBP') {
    const ratePpm = deriveSourceToFamilyRatePpm(
      sourceCurrencyCode,
      targetCurrencyCode,
      sourceRow?.ratePpm ?? null,
      null,
    );
    if (ratePpm === null || !sourceRow)
      throw new Error('The daily exchange-rate conversion was invalid.');
    return {
      sourceCurrencyCode,
      targetCurrencyCode,
      ratePpm,
      effectiveDate: sourceRow.effectiveDate,
      provider: 'frankfurter',
    };
  }
  const usdRow = rateSet.rates.get('USD');
  if (!usdRow) throw new Error('The shared daily USD rate was missing.');
  const ratePpm = deriveSourceToFamilyRatePpm(
    sourceCurrencyCode,
    targetCurrencyCode,
    sourceRow?.ratePpm ?? null,
    usdRow.ratePpm,
  );
  if (ratePpm === null) {
    throw new Error('The daily exchange-rate conversion was invalid.');
  }
  return {
    sourceCurrencyCode,
    targetCurrencyCode,
    ratePpm,
    effectiveDate:
      sourceRow && sourceRow.effectiveDate < usdRow.effectiveDate
        ? sourceRow.effectiveDate
        : usdRow.effectiveDate,
    provider: 'frankfurter',
  };
}

/** Derive source -> family PPM from the server's GBP -> quote rows. */
export function deriveSourceToFamilyRatePpm(
  sourceCurrencyCode: string,
  targetCurrencyCode: 'GBP' | 'USD',
  gbpToSourcePpm: number | null,
  gbpToUsdPpm: number | null,
): number | null {
  if (sourceCurrencyCode === targetCurrencyCode) return null;
  if (targetCurrencyCode === 'GBP') {
    return invertGbpQuoteRateToSourceToGbpPpm(gbpToSourcePpm ?? 0);
  }
  if (!Number.isSafeInteger(gbpToUsdPpm) || (gbpToUsdPpm ?? 0) <= 0) return null;
  if (sourceCurrencyCode === 'GBP') return gbpToUsdPpm;
  if (!Number.isSafeInteger(gbpToSourcePpm) || (gbpToSourcePpm ?? 0) <= 0) return null;
  const ratePpm = Math.round(((gbpToUsdPpm ?? 0) * 1_000_000) / (gbpToSourcePpm ?? 0));
  return Number.isSafeInteger(ratePpm) && ratePpm > 0 ? ratePpm : null;
}

export function parseSharedDailyFxRates(payload: unknown): {
  effectiveDate: string;
  rates: Map<string, { ratePpm: number; effectiveDate: string }>;
} {
  const root = asRecord(payload, 'Shared daily FX response');
  const baseCurrencyCode = requiredString(root, 'baseCurrencyCode', 'Shared daily FX response');
  const effectiveDate = requiredString(root, 'effectiveDate', 'Shared daily FX response');
  const provider = requiredString(root, 'provider', 'Shared daily FX response');
  if (
    baseCurrencyCode !== 'GBP' ||
    provider !== 'frankfurter' ||
    !/^\d{4}-\d{2}-\d{2}$/.test(effectiveDate) ||
    !Array.isArray(root.rates)
  ) {
    throw new Error('The shared daily exchange-rate response was invalid.');
  }
  const allowedCodes = new Set<string>(DAILY_GBP_QUOTE_CURRENCY_CODES);
  const rates = new Map<string, { ratePpm: number; effectiveDate: string }>();
  for (const value of root.rates) {
    const row = asRecord(value, 'Shared daily FX rate');
    const quoteCurrencyCode = requiredString(row, 'quoteCurrencyCode', 'Shared daily FX rate');
    const ratePpm = row.ratePpm;
    if (
      !allowedCodes.has(quoteCurrencyCode) ||
      typeof ratePpm !== 'number' ||
      !Number.isSafeInteger(ratePpm) ||
      ratePpm <= 0 ||
      rates.has(quoteCurrencyCode)
    ) {
      throw new Error('The shared daily exchange-rate response was invalid.');
    }
    rates.set(quoteCurrencyCode, { ratePpm, effectiveDate });
  }
  if (rates.size !== DAILY_GBP_QUOTE_CURRENCY_CODES.length) {
    throw new Error('The shared daily exchange-rate response was incomplete.');
  }
  return { effectiveDate, rates };
}

async function fetchSharedDailyFxRates(): Promise<ReturnType<typeof parseSharedDailyFxRates>> {
  const response = await fetch(SHARED_FX_RATES_URL, { headers: { accept: 'application/json' } });
  if (!response.ok) {
    throw new Error('Could not load ChorePop’s daily exchange rates.');
  }
  return parseSharedDailyFxRates(await response.json());
}

export async function fetchDailyFxRateToGbp(sourceCurrencyCode: string): Promise<CloudFxRateToGbp> {
  return fetchDailyFxRate(sourceCurrencyCode, 'GBP');
}

/** Convert a stored 1 GBP -> quote rate into a source -> GBP PPM rate. */
export function invertGbpQuoteRateToSourceToGbpPpm(gbpToSourcePpm: number): number | null {
  if (!Number.isSafeInteger(gbpToSourcePpm) || gbpToSourcePpm <= 0) return null;
  // Daily rows are GBP -> source. The ledger needs source -> GBP. Keep every
  // step integral and round to the nearest parts-per-million.
  const sourceToGbpPpm = Math.round((1_000_000 * 1_000_000) / gbpToSourcePpm);
  return Number.isSafeInteger(sourceToGbpPpm) && sourceToGbpPpm > 0 ? sourceToGbpPpm : null;
}

export type ParsedAuthCallback =
  | { kind: 'code'; code: string }
  | { kind: 'error'; message: string }
  | { kind: 'not_chorepop_auth' };

export function parseAuthCallback(rawUrl: string): ParsedAuthCallback {
  let callback: URL;
  try {
    callback = new URL(rawUrl);
  } catch {
    return { kind: 'not_chorepop_auth' };
  }

  let expectedCallback: URL;
  try {
    expectedCallback = new URL(CHOREPOP_AUTH_CALLBACK);
  } catch {
    return { kind: 'not_chorepop_auth' };
  }

  if (
    callback.protocol !== expectedCallback.protocol ||
    callback.hostname !== expectedCallback.hostname ||
    callback.pathname !== expectedCallback.pathname
  ) {
    return { kind: 'not_chorepop_auth' };
  }

  const providerError = callback.searchParams.get('error_description');
  if (providerError) return { kind: 'error', message: providerError };

  const code = callback.searchParams.get('code');
  if (!code) return { kind: 'error', message: 'The sign-in response did not contain a code.' };
  return { kind: 'code', code };
}

export async function exchangeAuthCallback(rawUrl: string): Promise<Session | null> {
  const parsed = parseAuthCallback(rawUrl);
  if (parsed.kind === 'not_chorepop_auth') return null;
  if (parsed.kind === 'error') throw new Error(parsed.message);

  const { data, error } = await getCloudAuthClient().auth.exchangeCodeForSession(parsed.code);
  if (error) throw error;
  return data.session;
}

export function isMissingNativeGoogleProvider(error: unknown): boolean {
  const message = error instanceof Error ? error.message : String(error);
  return /no provider dependencies found|no credential provider/i.test(message);
}

async function beginSupabaseOAuthSignIn(provider: CloudAuthProvider): Promise<null> {
  const { data, error } = await getCloudAuthClient().auth.signInWithOAuth({
    provider,
    options: {
      redirectTo: CHOREPOP_AUTH_CALLBACK,
      skipBrowserRedirect: true,
    },
  });
  if (error) throw error;
  if (!data.url) throw new Error('The sign-in provider did not return an authorization URL.');

  const authorizationUrl = new URL(data.url);
  const expectedAuthorizationHost = new URL(CHOREPOP_SUPABASE_URL).hostname;
  if (
    authorizationUrl.protocol !== 'https:' ||
    authorizationUrl.hostname !== expectedAuthorizationHost
  ) {
    throw new Error('Refusing to open an unexpected sign-in host.');
  }
  await openUrl(authorizationUrl.toString());
  return null;
}

export async function beginCloudSignIn(
  provider: CloudAuthProvider,
  platform = detectMobilePlatform(),
): Promise<Session | null> {
  if (!providersForPlatform(platform).includes(provider)) {
    throw new Error(`${provider} sign-in is not available on ${platform}.`);
  }

  if (nativeAuthAvailable(platform)) {
    if (provider === 'google') {
      if (googleSignInTransport(platform) === 'oauth') {
        return beginSupabaseOAuthSignIn('google');
      }
      let credential: { idToken?: string };
      try {
        credential = await invoke<{ idToken?: string }>('plugin:google-signin|sign_in', {
          clientId: nativeGoogleClientId(platform),
          serverClientId: configuredGoogleWebClientId,
        });
      } catch (error) {
        // Some Android tablets identify as Android but have no Google provider
        // installed. Fall back to the same OAuth/deep-link flow used by Fire OS
        // instead of surfacing a fatal Credential Manager error.
        if (isMissingNativeGoogleProvider(error)) {
          return beginSupabaseOAuthSignIn('google');
        }
        throw error;
      }
      const token = credential?.idToken?.trim();
      if (!token) throw new Error('Google sign-in returned no identity token.');
      const { data, error } = await getCloudAuthClient().auth.signInWithIdToken({
        provider: 'google',
        token,
      });
      if (error) throw error;
      if (!data.session) throw new Error('Google sign-in did not create a cloud session.');
      return data.session;
    }

    if (!nativeAppleAuthConfigured(platform)) {
      throw new Error('Apple sign-in is only available on iOS.');
    }
    const credential = await invoke<{ idToken?: string }>('plugin:siwa|sign_in');
    const token = credential?.idToken?.trim();
    if (!token) throw new Error('Apple sign-in returned no identity token.');
    const { data, error } = await getCloudAuthClient().auth.signInWithIdToken({
      provider: 'apple',
      token,
    });
    if (error) throw error;
    if (!data.session) throw new Error('Apple sign-in did not create a cloud session.');
    return data.session;
  }

  return beginSupabaseOAuthSignIn(provider);
}

export async function listenForCloudAuthCallbacks(
  onSession: (session: Session) => void,
  onError: (error: Error) => void,
): Promise<() => void> {
  const handleUrls = async (urls: string[]) => {
    for (const url of urls) {
      try {
        const session = await exchangeAuthCallback(url);
        if (session) onSession(session);
      } catch (unknownError) {
        onError(unknownError instanceof Error ? unknownError : new Error(String(unknownError)));
      }
    }
  };

  try {
    const current = await getCurrent();
    if (current) await handleUrls(current);
  } catch {
    // Browser-based component tests and desktop preview do not register the
    // mobile deep-link plugin. The real mobile listener below remains active.
  }

  try {
    return await onOpenUrl((urls) => {
      void handleUrls(urls);
    });
  } catch {
    return () => undefined;
  }
}

export async function getCloudSession(): Promise<Session | null> {
  const { data, error } = await getCloudAuthClient().auth.getSession();
  if (error) throw error;
  return data.session;
}

export type FamilyContentReportInput = {
  reporterLocalAccountId: number;
  reporterRole: 'guardian' | 'child';
  reportedLocalAccountId?: number;
  contentKind:
    | 'account'
    | 'chore'
    | 'routine'
    | 'routine_step'
    | 'goal'
    | 'deposit'
    | 'withdrawal'
    | 'approval'
    | 'other';
  contentLocalId?: string;
  reasonCode:
    | 'bullying_or_harassment'
    | 'threat_or_safety'
    | 'personal_information'
    | 'inappropriate_language'
    | 'sexual_or_child_safety'
    | 'hate_or_discrimination'
    | 'violence_or_illegal_content'
    | 'something_else';
  reporterNote?: string;
  contentSnapshot: Record<string, unknown>;
  reporterHidContent?: boolean;
};

const REPORT_REFERENCE_PATTERNS: Record<
  Exclude<FamilyContentReportInput['contentKind'], 'other'>,
  RegExp
> = {
  account: /^account:[1-9][0-9]*$/,
  chore: /^chore:[1-9][0-9]*$/,
  routine: /^(routine|routine-instance):[1-9][0-9]*$/,
  routine_step: /^routine-step:[1-9][0-9]*$/,
  goal: /^goal:[1-9][0-9]*$/,
  deposit: /^deposit:[1-9][0-9]*$/,
  withdrawal: /^withdrawal:[1-9][0-9]*$/,
  approval: /^(approval|transfer):[1-9][0-9]*$/,
};

function validateFamilyContentReportInput(input: FamilyContentReportInput): void {
  if (!Number.isSafeInteger(input.reporterLocalAccountId) || input.reporterLocalAccountId <= 0) {
    throw new Error('The active local reporter session is invalid.');
  }
  const reference = input.contentLocalId?.trim() ?? '';
  if (
    input.contentKind !== 'other' &&
    !REPORT_REFERENCE_PATTERNS[input.contentKind].test(reference)
  ) {
    throw new Error('Use Report on the exact family item so ChorePop can attach its record ID.');
  }
  let encodedSnapshot: string;
  try {
    encodedSnapshot = JSON.stringify(input.contentSnapshot);
  } catch {
    throw new Error('The family item snapshot could not be prepared.');
  }
  if (!encodedSnapshot || new TextEncoder().encode(encodedSnapshot).byteLength > 16_384) {
    throw new Error('The family item snapshot is too large to report safely.');
  }
}

export function buildFamilyContentReportRpc(
  bootstrap: CloudGuardianBootstrap,
  input: FamilyContentReportInput,
): {
  name: 'submit_family_content_report' | 'submit_trial_content_report';
  args: Record<string, unknown>;
} {
  validateFamilyContentReportInput(input);
  if (bootstrap.kind === 'family_selection_required') {
    throw new Error('Choose the linked ChorePop family before sending this private report.');
  }
  const commonArguments = {
    p_reporter_local_account_id: input.reporterLocalAccountId,
    p_reporter_role: input.reporterRole,
    p_reported_local_account_id: input.reportedLocalAccountId ?? null,
    p_content_kind: input.contentKind,
    p_content_local_id: input.contentLocalId?.trim() || null,
    p_reason_code: input.reasonCode,
    p_reporter_note: input.reporterNote?.trim() || null,
    p_content_snapshot: input.contentSnapshot,
    p_reporter_hid_content: input.reporterHidContent ?? false,
  };
  return bootstrap.kind === 'ready'
    ? {
        name: 'submit_family_content_report',
        args: { p_family_id: bootstrap.family.id, ...commonArguments },
      }
    : { name: 'submit_trial_content_report', args: commonArguments };
}

/**
 * Submit a private family-safety report to ChorePop's operator queue.
 *
 * The caller never receives queue contents or reporter metadata. Supabase RLS
 * exposes only the guarded RPC, which independently resolves the authenticated
 * guardian's family and stores an immutable snapshot for later moderation.
 * Local child/profile fields are context only; the verified guardian identity
 * remains the authoritative submitter in the operator queue.
 */
export async function submitFamilyContentReport(input: FamilyContentReportInput): Promise<string> {
  const session = await getCloudSession();
  if (!session) {
    throw new Error('Ask a guardian to connect ChorePop before sending this private report.');
  }
  const bootstrap = await bootstrapCloudGuardian({ expectedUserId: session.user.id });
  const rpc = buildFamilyContentReportRpc(bootstrap, input);
  const { data, error } = await getCloudAuthClient().rpc(rpc.name, rpc.args);
  queryError('Could not send the private report', error);
  if (typeof data !== 'string' || data.trim() === '') {
    throw new Error('The private report did not return a confirmation number.');
  }
  return data;
}

export type FamilyContentSafetyState = {
  familyId: string;
  familyLocked: boolean;
  action:
    | 'text_redacted'
    | 'content_removed'
    | 'account_suspended'
    | 'family_locked'
    | 'family_deletion_requested'
    | null;
  contentLocalId: string | null;
  targetedLocalAccountId: number | null;
};

export async function getFamilyContentSafetyState(
  client = getCloudAuthClient(),
): Promise<FamilyContentSafetyState[]> {
  const { data, error } = await client.rpc('family_content_safety_state');
  queryError('Could not check the family safety status', error);
  if (!Array.isArray(data)) throw new Error('The family safety status was invalid.');
  return data.map((value) => {
    const row = asRecord(value, 'Family safety status');
    const action = optionalString(row, 'action');
    if (
      action !== null &&
      ![
        'text_redacted',
        'content_removed',
        'account_suspended',
        'family_locked',
        'family_deletion_requested',
      ].includes(action)
    ) {
      throw new Error('The family safety action was invalid.');
    }
    const target = row.targeted_local_account_id;
    if (target !== null && target !== undefined && typeof target !== 'number') {
      throw new Error('The family safety account target was invalid.');
    }
    return {
      familyId: requiredString(row, 'family_id', 'Family safety status'),
      familyLocked: row.family_locked === true,
      action: action as FamilyContentSafetyState['action'],
      contentLocalId: optionalString(row, 'content_local_id'),
      targetedLocalAccountId: typeof target === 'number' ? target : null,
    };
  });
}

export async function requestFamilyDataDeletion(knownFamilyId?: string): Promise<{
  familyId: string;
  deleteAfter: string;
}> {
  const session = await getCloudSession();
  if (!session) {
    throw new Error('Sign in with a verified guardian identity before deleting data.');
  }
  const familyId =
    knownFamilyId ??
    (await (async () => {
      const bootstrap = await bootstrapCloudGuardian({ expectedUserId: session.user.id });
      if (bootstrap.kind !== 'ready') {
        throw new Error('Choose the linked ChorePop family before deleting data.');
      }
      return bootstrap.family.id;
    })());
  const { data, error } = await getCloudAuthClient().rpc('request_family_data_deletion', {
    p_family_id: familyId,
  });
  queryError('Could not schedule family data deletion', error);
  if (!Array.isArray(data) || data.length !== 1) {
    throw new Error('The deletion request returned an invalid recovery deadline.');
  }
  const row = asRecord(data[0], 'Family deletion request');
  return {
    familyId,
    deleteAfter: requiredString(row, 'delete_after', 'Family deletion request'),
  };
}

export async function getFamilyDataDeletionRecovery(client = getCloudAuthClient()): Promise<{
  familyId: string;
  deleteAfter: string;
} | null> {
  const { data, error } = await client.rpc('family_data_deletion_recovery_status');
  queryError('Could not check family data recovery', error);
  if (!Array.isArray(data) || data.length > 1) {
    throw new Error('The family deletion recovery state was invalid.');
  }
  if (data.length === 0) return null;
  const row = asRecord(data[0], 'Family deletion recovery');
  return {
    familyId: requiredString(row, 'family_id', 'Family deletion recovery'),
    deleteAfter: requiredString(row, 'delete_after', 'Family deletion recovery'),
  };
}

export async function cancelFamilyDataDeletion(
  familyId: string,
  client = getCloudAuthClient(),
): Promise<void> {
  const { error } = await client.rpc('cancel_family_data_deletion', {
    p_family_id: familyId,
  });
  queryError('Could not recover family data', error);
}

export async function signOutCloudSession(): Promise<void> {
  const { error } = await getCloudAuthClient().auth.signOut();
  if (error) throw error;
}
